import { amount, short, usd } from './format';
import type { TraderNotice } from './traders';

const key = (owner: string) => `soonscreener:browser-trade-alerts:${owner}`;
export const browserAlertsSupported = () => typeof Notification !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator;
export function browserAlertsEnabled(owner: string) { try { return browserAlertsSupported() && Notification.permission === 'granted' && localStorage.getItem(key(owner)) === 'on'; } catch { return false; } }
export async function browserAlertsRegistration() {
  const worker = await navigator.serviceWorker.register('/trader-alerts-sw.js');
  if (worker.active) return worker;
  const candidate = worker.installing ?? worker.waiting;
  if (!candidate) throw Error('Browser alerts could not start. Please try again.');
  await new Promise<void>((resolve, reject) => {
    const check = () => { if (candidate.state === 'activated') { clearTimeout(timer); candidate.removeEventListener('statechange', check); resolve(); } else if (candidate.state === 'redundant') { clearTimeout(timer); candidate.removeEventListener('statechange', check); reject(Error('Browser alerts could not start.')); } };
    const timer = setTimeout(() => { candidate.removeEventListener('statechange', check); reject(Error('Browser alerts took too long to start. Try again.')); }, 10_000);
    candidate.addEventListener('statechange', check); check();
  });
  return worker;
}
export async function enableBrowserTradeAlerts(owner: string) {
  if (!browserAlertsSupported()) throw Error('This browser does not support these alerts. Your in-app inbox is still available.');
  // Request directly from the click handler, before any asynchronous setup.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw Error(permission === 'denied' ? 'Browser notifications are blocked. You can allow them in this site’s browser settings.' : 'Browser notifications were not enabled.');
  await browserAlertsRegistration(); localStorage.setItem(key(owner), 'on');
}
export async function closeBrowserTradeAlerts(owner: string) {
  if (!('serviceWorker' in navigator)) return;
  const worker = await navigator.serviceWorker.getRegistration('/trader-alerts-sw.js');
  if (!worker) return;
  const notices = await worker.getNotifications();
  notices.filter(notice => notice.data?.account === owner).forEach(notice => notice.close());
}
export async function disableBrowserTradeAlerts(owner: string) {
  localStorage.removeItem(key(owner)); await closeBrowserTradeAlerts(owner);
}
export async function showBrowserTradeAlerts(owner: string, notices: TraderNotice[], stillSignedIn: () => boolean) {
  if (!browserAlertsEnabled(owner) || !stillSignedIn()) return;
  const worker = await browserAlertsRegistration();
  for (const notice of [...notices].sort((a, b) => a.id < b.id ? -1 : 1)) {
    if (!stillSignedIn() || !browserAlertsEnabled(owner)) return;
    const deliver = async () => {
      const cursorKey = `soonscreener:last-browser-trade:${owner}`;
      const last = localStorage.getItem(cursorKey);
      if (last && /^\d+$/.test(last) && BigInt(last) >= notice.id) return;
      if (!stillSignedIn()) return;
      await worker.showNotification(`${notice.buy ? 'Bought' : 'Sold'} ${notice.symbol}`, { body: `${short(notice.trader, 6)} ${notice.buy ? 'bought' : 'sold'} ${amount(notice.tokens)} ${notice.symbol}${notice.dollars === null ? '' : ` · ${usd(notice.dollars)}`}`, icon: '/brand/owl.png', badge: '/brand/owl.png', tag: `soonscreener:${owner}:${notice.id}`, data: { account: owner, url: `/traders/${notice.trader}` } });
      if (!stillSignedIn() || !browserAlertsEnabled(owner)) { await closeBrowserTradeAlerts(owner); return; }
      localStorage.setItem(cursorKey, notice.id.toString());
    };
    if ('locks' in navigator) await navigator.locks.request(`soonscreener-trade-alerts:${owner}`, deliver); else await deliver();
  }
}
